import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Services from '../../network/Services';

const { Option } = Select;

const DISPLAY_TYPES = ['BUTTON', 'CHECKBOX', 'INFO', 'ACTION', 'FORM'];
const ELEMENT_TYPES = ['TEXT', 'INFO_BOX', 'TEXTAREA', 'CONTACT_CARD', 'CHECKBOX', 'SELECT', 'BUTTON', 'FILE_UPLOAD'];

const emptyElement = () => ({
  elementId: 0,
  elementType: '',
  label: '',
  placeholder: '',
  defaultValue: '',
  styleClass: '',
  config: '',
  isRequired: false,
  orderNo: 0,
});

const AddOptionModal = ({ visible, onClose, onSubmit, editData, onDelete, categoryId }) => {
  const [text, setText] = useState('');
  const [description, setDescription] = useState('');
  const [displayType, setDisplayType] = useState('BUTTON');
  const [badge, setBadge] = useState('');
  const [badgeColor, setBadgeColor] = useState('');
  const [styleClass, setStyleClass] = useState('');
  const [icon, setIcon] = useState('');
  const [elements, setElements] = useState([]);
  const [nextNodeId, setNextNodeId] = useState(undefined);
  const [nextNodeOptions, setNextNodeOptions] = useState([]);
  const [docTypeId, setDocTypeId] = useState(undefined);
  const [docTypeOptions, setDocTypeOptions] = useState([]);
  const [docTypeLoading, setDocTypeLoading] = useState(false);
  const [headerButton, setHeaderButton] = useState('');
  const [link, setLink] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const isEdit = !!editData;

  useEffect(() => {
    if (visible) {
      setError('');
      setText(isEdit ? editData.optionText || '' : '');
      setDescription(isEdit ? editData.description || '' : '');
      setDisplayType(isEdit ? editData.displayType || 'BUTTON' : 'BUTTON');
      setBadge(isEdit ? editData.badge || '' : '');
      setBadgeColor(isEdit ? editData.badgeColor || '' : '');
      setStyleClass(isEdit ? editData.styleClass || '' : '');
      setIcon(isEdit ? editData.icon || '' : '');
      setElements(isEdit && editData.elements ? (() => {
        try {
          const parsed = typeof editData.elements === 'string' ? JSON.parse(editData.elements) : editData.elements;
          if (Array.isArray(parsed)) {
            return parsed?.map(el => ({
              elementId: el.ElementId || el.elementId || 0,
              elementType: el.ElementType || el.elementType || '',
              label: el.Label || el.label || '',
              placeholder: el.Placeholder || el.placeholder || '',
              defaultValue: el.DefaultValue || el.defaultValue || '',
              styleClass: el.StyleClass || el.styleClass || '',
              config: el.Config || el.config || '',
              isRequired: el.IsRequired || el.isRequired || false,
              orderNo: el.OrderNo || el.orderNo || 0,
            }));
          }
          return [];
        } catch { return []; }
      })() : []);
      setNextNodeId(isEdit && editData.nextNodeId ? editData.nextNodeId : undefined);
      setDocTypeId(isEdit && editData.docTypeId ? String(editData.docTypeId) : undefined);
      setHeaderButton(isEdit ? editData.headerButton || '' : '');
      setLink(isEdit ? editData.link || '' : '');
      if (categoryId) fetchNextNodeOptions();
      fetchDocTypes();
    }
  }, [visible, editData]);

  const fetchNextNodeOptions = async () => {
    try {
      const res = await Services.getAllStepsByCategoryId(categoryId);
      const data = (res?.data?.data || [])?.filter(n => n.isActive !== false);
      // Simply use the step data without fetching additional details
      setNextNodeOptions(data);
    } catch {
      setNextNodeOptions([]);
    }
  };

  const fetchDocTypes = async () => {
    setDocTypeLoading(true);
    try {
      const res = await Services.getDocTypeDropdown();
      setDocTypeOptions(res?.data?.data || []);
    } catch {
      setDocTypeOptions([]);
    } finally {
      setDocTypeLoading(false);
    }
  };

  const handleAddElement = () => {
    setElements(prev => [...prev, emptyElement()]);
  };

  const handleRemoveElement = (idx) => {
    setElements(prev => prev?.filter((_, i) => i !== idx));
  };

  const handleElementChange = (idx, field, value) => {
    setElements(prev => prev?.map((el, i) => i === idx ? { ...el, [field]: value } : el));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      await onSubmit({
        optionText: text.trim() || null,
        description: description.trim() || null,
        displayType: displayType || 'BUTTON',
        badge: badge.trim() || null,
        badgeColor: badgeColor.trim() || null,
        styleClass: styleClass.trim() || null,
        icon: icon.trim() || null,
        nextNodeId: nextNodeId || null,
        docTypeId: docTypeId ? Number(docTypeId) : null,
        headerButton: headerButton.trim() || null,
        link: link.trim() || null,
        elements: elements?.map((el, idx) => ({
          ...el,
          elementType: el.elementType || '',
          label: el.label || '',
          placeholder: el.placeholder || '',
          defaultValue: el.defaultValue || '',
          styleClass: el.styleClass || '',
          config: el.config || '',
          isRequired: !!el.isRequired,
          orderNo: el.orderNo || idx,
        })),
      });
      setText('');
      setDescription('');
      setElements([]);
    } catch (err) {
      const msg = err?.response?.data?.messages?.[0] || err?.response?.data?.Messages?.[0] || err?.message || 'Failed to save option. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: 16, color: '#474747', fontWeight: 'bold' }}>
          {isEdit ? 'Edit Option' : 'Add Option'}
        </span>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      className="ag-modal"
      width={700}
      bodyStyle={{ padding: '24px 28px', borderTop: '1px solid #bfbfbf', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      {/* Option Text */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
          Option Text <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
        </label>
        <Input
          placeholder="e.g., Yes, proceed"
          value={text}
          onChange={(e) => { setText(e.target.value); if (error) setError(''); }}
          style={{ marginTop: 6, borderRadius: 4, borderColor: error ? '#ff4d4f' : '#d9d9d9', height: 38 }}
        />
        {error && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{error}</p>}
      </div>

      {/* Description */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
          Description <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
        </label>
        <Input.TextArea
          placeholder="Brief description for this option"
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={2}
          style={{ marginTop: 6, borderRadius: 4 }}
        />
      </div>

      {/* Display Type */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>Display Type</label>
        <Select
          placeholder="Select display type"
          value={displayType}
          onChange={(val) => setDisplayType(val)}
          style={{ width: '100%', marginTop: 6 }}
          size="middle"
        >
          {DISPLAY_TYPES?.map(dt => (
            <Option key={dt} value={dt}>{dt}</Option>
          ))}
        </Select>
      </div>

      {/* Badge & Icon row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
            Badge <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <Input
            placeholder="e.g., New"
            value={badge}
            onChange={e => setBadge(e.target.value)}
            style={{ marginTop: 6, borderRadius: 4, height: 38 }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
            Badge Color <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <Input
            placeholder="e.g., #ff0000"
            value={badgeColor}
            onChange={e => setBadgeColor(e.target.value)}
            style={{ marginTop: 6, borderRadius: 4, height: 38 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
            Icon <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <Input
            placeholder="Icon name or URL"
            value={icon}
            onChange={e => setIcon(e.target.value)}
            style={{ marginTop: 6, borderRadius: 4, height: 38 }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
            Style Class <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <Input
            placeholder="e.g., btn-primary"
            value={styleClass}
            onChange={e => setStyleClass(e.target.value)}
            style={{ marginTop: 6, borderRadius: 4, height: 38 }}
          />
        </div>
      </div>

      {/* Next Node - Now shown for both add and edit */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
          Next Node <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
        </label>
        <Select
          allowClear
          showSearch
          placeholder="Select next node"
          value={nextNodeId != null ? nextNodeId : undefined}
          onChange={(val) => setNextNodeId(val != null ? val : null)}
          onClear={() => setNextNodeId(null)}
          optionFilterProp="children"
          style={{ width: '100%', marginTop: 6 }}
          size="middle"
        >
          {nextNodeOptions?.map(n => (
            <Option key={n.nodeId} value={n.nodeId}>{n.name} ({n.nodeTypeName})</Option>
          ))}
        </Select>
      </div>

      {/* Link field */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
          Link <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional - for external URLs or routes)</span>
        </label>
        <Input
          placeholder="e.g., /dashboard or https://example.com"
          value={link}
          onChange={e => setLink(e.target.value)}
          style={{ marginTop: 6, borderRadius: 4, height: 38 }}
        />
      </div>

      {/* Elements Section */}
      {/* Doc Type & Header Button */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
            Doc Type <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <Select
            allowClear
            showSearch
            placeholder="Select document type"
            value={docTypeId || undefined}
            onChange={(val) => setDocTypeId(val)}
            loading={docTypeLoading}
            optionFilterProp="children"
            style={{ width: '100%', marginTop: 6 }}
            size="middle"
          >
            {docTypeOptions?.map(opt => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 500 }}>
            Header Button <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <Input
            placeholder="e.g., View Document"
            value={headerButton}
            onChange={e => setHeaderButton(e.target.value)}
            style={{ marginTop: 6, borderRadius: 4, height: 38 }}
          />
        </div>
      </div>

      {/* Elements Section */}
      <div style={{ marginBottom: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: '#333', fontWeight: 600 }}>
            Elements <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
          </label>
          <Button
            size="small"
            onClick={handleAddElement}
            icon={<PlusOutlined />}
            style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 4, fontSize: 12 }}
          >
            Add
          </Button>
        </div>

        {elements?.length === 0 && (
          <p style={{ color: '#aaa', fontSize: 12, margin: '4px 0 0', fontStyle: 'italic' }}>No elements added yet.</p>
        )}

        {elements?.map((el, idx) => (
          <div
            key={idx}
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: 6,
              padding: '12px 14px',
              marginBottom: 10,
              background: '#fafafa',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#720C20' }}>Element {idx + 1}</span>
              <DeleteOutlined
                style={{ color: '#720C20', cursor: 'pointer', fontSize: 13 }}
                onClick={() => handleRemoveElement(idx)}
              />
            </div>

            {/* Type & Label */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Type</label>
                <Select
                  placeholder="Select"
                  value={el.elementType || undefined}
                  onChange={(val) => handleElementChange(idx, 'elementType', val)}
                  style={{ width: '100%' }}
                  size="small"
                >
                  {ELEMENT_TYPES?.map(et => (
                    <Option key={et} value={et}>{et}</Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Label</label>
                <Input
                  size="small"
                  placeholder="Label"
                  value={el.label}
                  onChange={e => handleElementChange(idx, 'label', e.target.value)}
                  style={{ borderRadius: 4 }}
                />
              </div>
            </div>

            {/* Placeholder & Default Value */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Placeholder</label>
                <Input
                  size="small"
                  placeholder="Placeholder text"
                  value={el.placeholder}
                  onChange={e => handleElementChange(idx, 'placeholder', e.target.value)}
                  style={{ borderRadius: 4 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Default Value</label>
                <Input
                  size="small"
                  placeholder="Default value"
                  value={el.defaultValue}
                  onChange={e => handleElementChange(idx, 'defaultValue', e.target.value)}
                  style={{ borderRadius: 4 }}
                />
              </div>
            </div>

            {/* Config & Style */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Config (JSON)</label>
                <Input
                  size="small"
                  placeholder='{"key":"value"}'
                  value={el.config}
                  onChange={e => handleElementChange(idx, 'config', e.target.value)}
                  style={{ borderRadius: 4 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Style Class</label>
                <Input
                  size="small"
                  placeholder="CSS class"
                  value={el.styleClass}
                  onChange={e => handleElementChange(idx, 'styleClass', e.target.value)}
                  style={{ borderRadius: 4 }}
                />
              </div>
            </div>

            {/* Order & Required */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Order</label>
                <Input
                  size="small"
                  type="number"
                  placeholder="0"
                  value={el.orderNo}
                  onChange={e => handleElementChange(idx, 'orderNo', Number(e.target.value) || 0)}
                  style={{ borderRadius: 4, width: 70 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16 }}>
                <input
                  type="checkbox"
                  checked={!!el.isRequired}
                  onChange={e => handleElementChange(idx, 'isRequired', e.target.checked)}
                  style={{ accentColor: '#720C20' }}
                />
                <label style={{ fontSize: 12, color: '#555' }}>Required</label>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: isEdit ? 'space-between' : 'flex-end', alignItems: 'center', gap: 10, marginTop: 20, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
        {isEdit && onDelete && (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setConfirmVisible(true)}
            disabled={loading}
            style={{
              borderRadius: 4,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              borderColor: '#720C20',
              color: '#720C20',
            }}
          >
            Delete
          </Button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 4, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            loading={loading}
            onClick={handleSubmit}
            style={{ backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff', borderRadius: 4, fontWeight: 500 }}
          >
            {isEdit ? 'Update Option' : 'Add Option'}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <Modal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={null}
        centered
        width={420}
        zIndex={1200}
        closable
        className="ag-modal"
        bodyStyle={{ padding: '28px 28px 20px' }}
      >
        <p style={{ fontSize: 15, color: '#333', marginBottom: 20, textAlign: 'center' }}>
          Are you sure you want to delete <strong>{editData?.optionText}</strong>?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button onClick={() => setConfirmVisible(false)} style={{ minWidth: 100, height: 36, borderRadius: 4, borderColor: '#720C20', color: '#720C20' }}>Cancel</Button>
          <Button
            onClick={() => { setConfirmVisible(false); onDelete(editData.optionId); }}
            style={{ minWidth: 100, height: 36, borderRadius: 4, backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff' }}
          >Delete</Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default AddOptionModal;
